import { useCallback, useEffect, useRef, useState } from "react";
import { registerLearningMediaCleanup, setLearningMediaActive } from "./learning-media";
import { stopLocalAudio } from "./local-audio";
import { stopSpeech } from "./speech";

export type LearningSpeechPhase="idle"|"starting"|"listening"|"stopping"|"preview"|"error";
export type LearningSpeechBackend="recognition"|"recorder";

type RecognitionResultLike={isFinal?:boolean;0?:{transcript?:string}};
type RecognitionEventLike={results:ArrayLike<RecognitionResultLike>};
type RecognitionErrorLike={error:string};
type RecognitionLike={
  lang:string;
  continuous:boolean;
  interimResults:boolean;
  maxAlternatives:number;
  start:()=>void;
  stop:()=>void;
  abort:()=>void;
  onstart:(()=>void)|null;
  onresult:((event:RecognitionEventLike)=>void)|null;
  onerror:((event:RecognitionErrorLike)=>void)|null;
  onend:(()=>void)|null;
};
type RecognitionConstructor=new()=>RecognitionLike;

let speechSessionId=0;

function recognitionConstructor(){
  if(typeof window==="undefined")return null;
  const speechWindow=window as typeof window&{SpeechRecognition?:RecognitionConstructor;webkitSpeechRecognition?:RecognitionConstructor};
  return speechWindow.SpeechRecognition||speechWindow.webkitSpeechRecognition||null;
}

function recognitionErrorMessage(error:string){
  if(error==="not-allowed"||error==="service-not-allowed")return"麦克风或语音识别权限未开启，请在浏览器网站设置中允许后重试。";
  if(error==="audio-capture")return"没有检测到可用麦克风，请检查系统录音权限或蓝牙设备。";
  if(error==="network")return"浏览器语音识别网络暂时不可用，已切换为录音回放。";
  if(error==="language-not-supported")return"当前浏览器不能识别这门语言，已切换为录音回放。";
  if(error==="no-speech")return"没有听清你说的话，请靠近麦克风后再试一次。";
  return"语音识别没有正常结束，已切换为录音回放。";
}

function recorderErrorMessage(error:unknown){
  const name=typeof DOMException!=="undefined"&&error instanceof DOMException?error.name:"";
  if(name==="NotAllowedError"||name==="SecurityError")return"麦克风权限未开启，请在浏览器网站设置中允许后重试。";
  if(name==="NotFoundError"||name==="DevicesNotFoundError")return"没有检测到可用麦克风，请检查系统录音权限或蓝牙设备。";
  if(name==="NotReadableError"||name==="TrackStartError")return"麦克风正在被其他应用占用，请关闭其他录音后重试。";
  return"录音没有成功启动，请检查麦克风权限后重试。";
}

function stopTracks(stream:MediaStream|null){
  stream?.getTracks().forEach(track=>{try{track.stop()}catch{/* Track may already be stopped. */}});
}

export function useLearningSpeechSession({locale,maxDurationMs=20_000,onTranscript}:{locale:string;maxDurationMs?:number;onTranscript?:(transcript:string)=>void}){
  const initialBackend=recognitionConstructor()?"recognition":"recorder";
  const[phase,setPhase]=useState<LearningSpeechPhase>("idle"),[backend,setBackend]=useState<LearningSpeechBackend>(initialBackend),[transcript,setTranscript]=useState(""),[audioUrl,setAudioUrl]=useState(""),[error,setError]=useState(""),[notice,setNotice]=useState("");
  const phaseRef=useRef<LearningSpeechPhase>("idle"),backendRef=useRef<LearningSpeechBackend>(initialBackend),transcriptRef=useRef(""),recognitionRef=useRef<RecognitionLike|null>(null),recorderRef=useRef<MediaRecorder|null>(null),streamRef=useRef<MediaStream|null>(null),chunksRef=useRef<Blob[]>([]),sessionRef=useRef(0),settledRef=useRef(0),timeoutRef=useRef<number|null>(null),finishTimeoutRef=useRef<number|null>(null),audioUrlRef=useRef(""),mountedRef=useRef(true),onTranscriptRef=useRef(onTranscript),sourceRef=useRef(`learning-speech-${++speechSessionId}`);

  useEffect(()=>{onTranscriptRef.current=onTranscript},[onTranscript]);

  const updatePhase=useCallback((next:LearningSpeechPhase)=>{phaseRef.current=next;if(mountedRef.current)setPhase(next)},[]);
  const clearTimers=useCallback(()=>{if(timeoutRef.current!==null)window.clearTimeout(timeoutRef.current);if(finishTimeoutRef.current!==null)window.clearTimeout(finishTimeoutRef.current);timeoutRef.current=null;finishTimeoutRef.current=null},[]);
  const revokeAudio=useCallback(()=>{if(audioUrlRef.current){URL.revokeObjectURL(audioUrlRef.current);audioUrlRef.current="";if(mountedRef.current)setAudioUrl("")}},[]);
  const setInactive=useCallback(()=>setLearningMediaActive(sourceRef.current,false),[]);

  const settleRecognition=useCallback((session:number,errorCode="")=>{
    if(session!==sessionRef.current||settledRef.current===session)return;
    settledRef.current=session;clearTimers();recognitionRef.current=null;setInactive();
    const value=transcriptRef.current.trim();
    if(errorCode){
      const shouldFallback=!['not-allowed','service-not-allowed','audio-capture','no-speech','aborted'].includes(errorCode);
      if(shouldFallback){backendRef.current="recorder";if(mountedRef.current)setBackend("recorder")}
      if(mountedRef.current){setError(recognitionErrorMessage(errorCode));setNotice("")}
      updatePhase("error");return;
    }
    if(value){
      if(mountedRef.current){setTranscript(value);setError("")}
      onTranscriptRef.current?.(value);updatePhase("preview");return;
    }
    if(mountedRef.current){setError(recognitionErrorMessage("no-speech"));setNotice("")}
    updatePhase("error");
  },[clearTimers,setInactive,updatePhase]);

  const settleRecorder=useCallback((session:number,problem?:unknown)=>{
    if(session!==sessionRef.current||settledRef.current===session)return;
    settledRef.current=session;clearTimers();const media=recorderRef.current;recorderRef.current=null;stopTracks(streamRef.current);streamRef.current=null;setInactive();
    if(problem){if(mountedRef.current){setError(recorderErrorMessage(problem));setNotice("")}updatePhase("error");return}
    const blob=new Blob(chunksRef.current,{type:media?.mimeType||"audio/webm"});
    if(!blob.size){if(mountedRef.current)setError("没有录到声音，请检查麦克风后再试一次。");updatePhase("error");return}
    revokeAudio();const nextUrl=URL.createObjectURL(blob);audioUrlRef.current=nextUrl;
    if(mountedRef.current){setAudioUrl(nextUrl);setError("");setNotice("录音已经停止，可以回放后再确认答案。")}
    updatePhase("preview");
  },[clearTimers,revokeAudio,setInactive,updatePhase]);

  const stop=useCallback((reason:"manual"|"timeout"="manual")=>{
    if(phaseRef.current!=="listening")return;
    updatePhase("stopping");clearTimers();
    if(reason==="timeout"&&mountedRef.current)setNotice("已到 20 秒上限，正在结束录音…");
    const session=sessionRef.current;
    if(backendRef.current==="recognition"){
      const recognition=recognitionRef.current;
      if(!recognition){settleRecognition(session);return}
      try{recognition.stop()}catch{try{recognition.abort()}catch{/* Already ended. */}settleRecognition(session)}
      if(settledRef.current!==session)finishTimeoutRef.current=window.setTimeout(()=>{try{recognition.abort()}catch{/* Already ended. */}settleRecognition(session)},1_600);
      return;
    }
    const recorder=recorderRef.current;
    if(!recorder||recorder.state==="inactive"){settleRecorder(session);return}
    try{recorder.stop()}catch(error){settleRecorder(session,error)}
  },[clearTimers,settleRecognition,settleRecorder,updatePhase]);
  const stopRef=useRef(stop);useEffect(()=>{stopRef.current=stop},[stop]);

  const startRecognition=useCallback(()=>{
    const Constructor=recognitionConstructor();if(!Constructor)return false;
    const session=++sessionRef.current;settledRef.current=0;transcriptRef.current="";if(mountedRef.current){setTranscript("");setError("");setNotice("")}
    const recognition=new Constructor();recognitionRef.current=recognition;recognition.lang=locale;recognition.continuous=false;recognition.interimResults=true;recognition.maxAlternatives=1;
    recognition.onstart=()=>{if(session===sessionRef.current)updatePhase("listening")};
    recognition.onresult=event=>{if(session!==sessionRef.current)return;let finalText="",interimText="";for(let index=0;index<event.results.length;index++){const result=event.results[index],value=result[0]?.transcript||"";if(result.isFinal===false)interimText+=value;else finalText+=value}const value=`${finalText}${interimText}`.trim();transcriptRef.current=value;if(mountedRef.current)setTranscript(value)};
    recognition.onerror=event=>{if(event.error!=="aborted")settleRecognition(session,event.error)};
    recognition.onend=()=>settleRecognition(session);
    updatePhase("starting");setLearningMediaActive(sourceRef.current,true);
    try{recognition.start();updatePhase("listening");timeoutRef.current=window.setTimeout(()=>stopRef.current("timeout"),maxDurationMs);return true}catch{
      recognitionRef.current=null;setInactive();backendRef.current="recorder";if(mountedRef.current){setBackend("recorder");setError("语音识别没有成功启动，已切换为录音回放。")}updatePhase("error");return false;
    }
  },[locale,maxDurationMs,setInactive,settleRecognition,updatePhase]);

  const startRecorder=useCallback(async()=>{
    if(!navigator.mediaDevices?.getUserMedia||typeof MediaRecorder==="undefined"){if(mountedRef.current)setError("当前浏览器无法使用麦克风录音，请改为直接输入答案。");updatePhase("error");return}
    const session=++sessionRef.current;settledRef.current=0;chunksRef.current=[];if(mountedRef.current){setError("");setNotice("")}updatePhase("starting");
    try{
      const stream=await navigator.mediaDevices.getUserMedia({audio:true});
      if(session!==sessionRef.current){stopTracks(stream);return}
      streamRef.current=stream;const mime=typeof MediaRecorder.isTypeSupported==="function"?(MediaRecorder.isTypeSupported("audio/mp4")?"audio/mp4":MediaRecorder.isTypeSupported("audio/webm")?"audio/webm":""):"",recorder=new MediaRecorder(stream,mime?{mimeType:mime}:undefined);recorderRef.current=recorder;
      recorder.ondataavailable=event=>{if(session===sessionRef.current&&event.data.size)chunksRef.current.push(event.data)};
      recorder.onerror=event=>settleRecorder(session,event);
      recorder.onstop=()=>settleRecorder(session);
      recorder.start();updatePhase("listening");setLearningMediaActive(sourceRef.current,true);timeoutRef.current=window.setTimeout(()=>stopRef.current("timeout"),maxDurationMs);
    }catch(problem){stopTracks(streamRef.current);streamRef.current=null;settleRecorder(session,problem)}
  },[maxDurationMs,settleRecorder,updatePhase]);

  const start=useCallback(async()=>{
    if(["starting","listening","stopping"].includes(phaseRef.current))return;
    stopSpeech();stopLocalAudio();revokeAudio();
    if(backendRef.current==="recognition"&&startRecognition())return;
    if(backendRef.current==="recorder")await startRecorder();
  },[revokeAudio,startRecognition,startRecorder]);

  const cancelInternal=useCallback((updateState:boolean)=>{
    sessionRef.current+=1;clearTimers();const recognition=recognitionRef.current;recognitionRef.current=null;if(recognition){recognition.onstart=null;recognition.onresult=null;recognition.onerror=null;recognition.onend=null;try{recognition.abort()}catch{/* Already ended. */}}
    const recorder=recorderRef.current;recorderRef.current=null;if(recorder){recorder.ondataavailable=null;recorder.onerror=null;recorder.onstop=null;if(recorder.state!=="inactive")try{recorder.stop()}catch{/* Already ended. */}}
    stopTracks(streamRef.current);streamRef.current=null;chunksRef.current=[];setInactive();revokeAudio();transcriptRef.current="";phaseRef.current="idle";
    if(updateState&&mountedRef.current){setPhase("idle");setTranscript("");setError("");setNotice("")}
  },[clearTimers,revokeAudio,setInactive]);
  const cancel=useCallback(()=>cancelInternal(true),[cancelInternal]);

  const clear=useCallback(()=>{revokeAudio();transcriptRef.current="";if(mountedRef.current){setTranscript("");setError("");setNotice("")}updatePhase("idle")},[revokeAudio,updatePhase]);

  useEffect(()=>{
    mountedRef.current=true;const unregister=registerLearningMediaCleanup(cancel);
    const hide=()=>cancel();const visibility=()=>{if(document.visibilityState==="hidden")cancel()};
    window.addEventListener("pagehide",hide);document.addEventListener("visibilitychange",visibility);
    return()=>{unregister();window.removeEventListener("pagehide",hide);document.removeEventListener("visibilitychange",visibility);mountedRef.current=false;cancelInternal(false)};
  },[cancel,cancelInternal]);

  return{phase,backend,transcript,audioUrl,error,notice,isActive:phase==="starting"||phase==="listening"||phase==="stopping",start,stop,cancel,clear};
}
